import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, Lock, Share2, Image as ImageIcon, ChevronLeft, ChevronRight, Menu, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = ({ user }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [configLoading, setConfigLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Start with null/empty values - NO defaults that could flash
  const [config, setConfig] = useState({
    hero_title: '',
    hero_subtitle: '',
    brand_name: '',
    brand_tagline: '',
    hero_image_1: null,
    hero_image_2: null,
    hero_image_3: null,
    hero_image_4: null,
    hero_image_5: null,
    hero_image_6: null,
    hero_image_7: null,
    hero_image_8: null,
    hero_image_9: null,
    hero_image_10: null
  });

  // Get all hero images that have values
  const heroImages = [
    config.hero_image_1,
    config.hero_image_2,
    config.hero_image_3,
    config.hero_image_4,
    config.hero_image_5,
    config.hero_image_6,
    config.hero_image_7,
    config.hero_image_8,
    config.hero_image_9,
    config.hero_image_10
  ].filter(img => img && img.trim() !== '');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/public/landing-config`);
        // Set config from API response, with fallbacks only if API returns empty
        setConfig({
          hero_title: response.data.hero_title || 'Welcome',
          hero_subtitle: response.data.hero_subtitle || '',
          brand_name: response.data.brand_name || 'Gallery',
          brand_tagline: response.data.brand_tagline || '',
          hero_image_1: response.data.hero_image_1 || null,
          hero_image_2: response.data.hero_image_2 || null,
          hero_image_3: response.data.hero_image_3 || null,
          hero_image_4: response.data.hero_image_4 || null,
          hero_image_5: response.data.hero_image_5 || null,
          hero_image_6: response.data.hero_image_6 || null,
          hero_image_7: response.data.hero_image_7 || null,
          hero_image_8: response.data.hero_image_8 || null,
          hero_image_9: response.data.hero_image_9 || null,
          hero_image_10: response.data.hero_image_10 || null
        });
      } catch (error) {
        console.error('Failed to load landing config');
        // Only use generic fallbacks on error - nothing that looks like a template
        setConfig(prev => ({ 
          ...prev, 
          hero_title: 'Welcome',
          brand_name: 'Gallery' 
        }));
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + heroImages.length) % heroImages.length);
  };

  // Get image URL (handles both uploaded and external URLs)
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('/api')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    return imageUrl;
  };

  // Parse hero title for line breaks
  const renderHeroTitle = () => {
    // Handle period-separated phrases like "Every moment. Every perspective. One gallery."
    if (config.hero_title && config.hero_title.includes('. ')) {
      const phrases = config.hero_title.split('. ');
      return (
        <>
          {phrases.map((phrase, index) => (
            <span key={index}>
              {phrase}{index < phrases.length - 1 ? '.' : ''}
              {index < phrases.length - 1 && <br />}
            </span>
          ))}
        </>
      );
    }
    
    // Handle comma-separated titles
    const parts = config.hero_title.split(',');
    if (parts.length > 1) {
      return (
        <>
          {parts[0]},
          <br />
          {parts[1]}
        </>
      );
    }
    return config.hero_title;
  };

  // Show elegant loading state while fetching config
  if (configLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-zinc-300 border-t-zinc-800 rounded-full mx-auto mb-4"
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white relative">
      <nav className="border-b border-zinc-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex justify-between items-center">
          {/* Brand name on left - premium look */}
          <div className="text-left">
            {/* Smart brand name parsing - if contains " by " and no tagline, split it */}
            {(() => {
              let brandMain = config.brand_name;
              let tagline = config.brand_tagline;
              
              // Auto-split if brand_name contains " by " and no separate tagline
              if (!tagline && config.brand_name && config.brand_name.includes(' by ')) {
                const parts = config.brand_name.split(' by ');
                brandMain = parts[0];
                tagline = 'by ' + parts.slice(1).join(' by ');
              }
              
              return (
                <>
                  <h1 
                    className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {brandMain}
                  </h1>
                  {tagline && (
                    <p 
                      className="text-[9px] sm:text-[10px] md:text-xs text-zinc-400 mt-0.5 tracking-widest uppercase"
                      style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.12em' }}
                    >
                      {tagline}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-4">
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm text-zinc-600 hover:text-zinc-900"
            >
              Pricing
            </button>
            {user ? (
              <button
                data-testid="nav-dashboard-button"
                onClick={() => navigate('/dashboard')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 md:px-8 rounded-sm font-medium tracking-wide transition-all duration-300 text-sm md:text-base"
              >
                Dashboard
              </button>
            ) : (
              <button
                data-testid="nav-login-button"
                onClick={() => navigate('/auth')}
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 md:px-8 rounded-sm font-medium tracking-wide transition-all duration-300 text-sm md:text-base"
              >
                Get Started
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden p-2 hover:bg-zinc-100 rounded-sm transition-colors"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="sm:hidden border-t border-zinc-200 bg-white"
            >
              <div className="px-4 py-4 space-y-3">
                <button
                  onClick={() => {
                    navigate('/pricing');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left py-3 px-4 text-zinc-700 hover:bg-zinc-50 rounded-sm transition-colors font-medium"
                >
                  Pricing
                </button>
                {user ? (
                  <button
                    onClick={() => {
                      navigate('/dashboard');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 px-4 rounded-sm font-medium transition-colors"
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      navigate('/auth');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-3 px-4 rounded-sm font-medium transition-colors"
                  >
                    Get Started
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 text-center lg:text-left"
          >
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-4 sm:mb-6">
              Professional Photo Sharing
            </p>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium tracking-tight leading-tight mb-4 sm:mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {renderHeroTitle()}
            </h1>
            <p className="text-base sm:text-lg md:text-xl font-light leading-relaxed text-zinc-600 mb-8 sm:mb-12 max-w-xl mx-auto lg:mx-0">
              {/* Format subtitle with line breaks for phrases ending with periods */}
              {config.hero_subtitle && config.hero_subtitle.includes('. ') ? (
                config.hero_subtitle.split('. ').map((phrase, index, arr) => (
                  <span key={index} className="block">
                    {phrase}{index < arr.length - 1 ? '.' : ''}
                  </span>
                ))
              ) : (
                config.hero_subtitle
              )}
            </p>
            <button
              data-testid="hero-get-started-button"
              onClick={() => navigate(user ? '/dashboard' : '/auth')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 sm:h-12 px-8 sm:px-12 rounded-sm font-medium tracking-wide transition-all duration-300 text-base sm:text-lg"
            >
              {user ? 'Go to Dashboard' : 'Get Started'}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-6 order-first lg:order-last"
          >
            {/* Image Carousel */}
            {heroImages.length > 0 ? (
              <div className="relative">
                <div className="relative overflow-hidden rounded-sm shadow-lg aspect-[4/3]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentSlide}
                      src={getImageUrl(heroImages[currentSlide])}
                      alt={`Gallery sample ${currentSlide + 1}`}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.5 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        console.error('Failed to load hero image:', heroImages[currentSlide]);
                      }}
                    />
                  </AnimatePresence>
                </div>
                
                {/* Carousel Controls */}
                {heroImages.length > 1 && (
                  <>
                    <button
                      onClick={prevSlide}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition-colors"
                      data-testid="carousel-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition-colors"
                      data-testid="carousel-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    
                    {/* Dots indicator */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {heroImages.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentSlide(idx)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === currentSlide 
                              ? 'bg-white w-6' 
                              : 'bg-white/50 hover:bg-white/75'
                          }`}
                          data-testid={`carousel-dot-${idx}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Fallback placeholder */
              <div className="aspect-[4/3] bg-zinc-100 rounded-sm flex items-center justify-center">
                <ImageIcon className="w-16 h-16 text-zinc-300" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="bg-zinc-50/30 py-16 sm:py-20 md:py-24 lg:py-32">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-4 sm:mb-6 text-center">
            Features
          </p>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight text-center mb-12 sm:mb-16 md:mb-20"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Everything you need
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-zinc-200 rounded-sm">
                <Camera className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl md:text-3xl font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Create Galleries
              </h3>
              <p className="text-base font-light leading-relaxed text-zinc-600">
                Organize your photos into beautiful galleries and share them with clients effortlessly.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-zinc-200 rounded-sm">
                <Upload className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl md:text-3xl font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Guest Uploads
              </h3>
              <p className="text-base font-light leading-relaxed text-zinc-600">
                Let your clients upload their own photos directly to your gallery via a simple link.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center border border-zinc-200 rounded-sm">
                <Lock className="w-8 h-8" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl md:text-3xl font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Password Protection
              </h3>
              <p className="text-base font-light leading-relaxed text-zinc-600">
                Keep your galleries private with optional password protection for added security.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-20 md:py-24 lg:py-32">
        <div className="bg-zinc-50/50 rounded-sm p-8 sm:p-12 md:p-20 text-center">
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight mb-4 sm:mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Ready to share your work?
          </h2>
          <p className="text-base sm:text-lg md:text-xl font-light leading-relaxed text-zinc-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Join photographers who trust {config.brand_name || 'us'} for their client galleries.
          </p>
          <button
            data-testid="cta-get-started-button"
            onClick={() => navigate('/auth')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 sm:h-12 px-8 sm:px-12 rounded-sm font-medium tracking-wide transition-all duration-300 text-base sm:text-lg"
          >
            Get Started Free
          </button>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8 sm:py-12">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-zinc-500">
          <a 
            href="/admin" 
            className="text-zinc-400 hover:text-zinc-600 transition-colors order-2 sm:order-1"
            data-testid="admin-link"
          >
            Admin
          </a>
          <p className="order-1 sm:order-2 text-center">© 2024 {config.brand_name}. Built for photographers.</p>
          <div className="hidden sm:block w-12 order-3"></div> {/* Spacer for balance on desktop */}
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;